import { DeviceInfoManager } from '@sekoia/shared/utils/DeviceInfoManager';
import base64url from 'base64url';
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useIsNativeFromPath, useIsResidentFromPath } from 'src/hooks/useIsNative';
import { useMountUnmountLogger } from 'src/hooks/useMountUnmountLogger';
import { Authentication } from 'src/utils/authentication/Authentication';
import { SilentRefresh } from 'src/utils/authentication/SilentRefresh';
import { ai } from '@sekoia/shared/utils/telemetryService';
import { getPublicKey } from 'src/utils/nativeCommunication';
import PinWrapper from './Pin/Wrapper/PinWrapper';
import LoadingSpinner from './_Layout/LoadingSpinner';
import { User } from 'oidc-client';
import { ResidentAuthentication } from 'src/utils/authentication/ResidentAuthentication';
import { ApplicationInsightTrackKeys } from 'src/constants/ApplicationInsightTrackKeys';
import { SessionKeys } from 'src/constants/SessionKeys';

interface AuthContextType {
  isPinHidden: boolean;
  showPin: () => void;
  hidePin: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    // Return default context when AuthProvider is not present
    return {
      isPinHidden: true,
      showPin: () => {},
      hidePin: () => {},
    };
  }
  return context;
};

type Props = {
  children: ReactNode;
  isResidentEntrance?: boolean;
};

const AuthProvider = ({ children, isResidentEntrance = false }: Props) => {
  useMountUnmountLogger('AuthProvider');

  const isNative = useIsNativeFromPath();
  const isResident = useIsResidentFromPath();
  const [isInitiallyAuthenticated, setIsInitiallyAuthenticated] = useState(false);
  const [isPinHidden, setIsPinHidden] = useState<boolean>(() => {
    const stored = sessionStorage.getItem(SessionKeys.HIDE_PIN);
    return stored === null ? true : stored === 'true';
  });

  const handleHidePin = useCallback(() => {
    sessionStorage.setItem(SessionKeys.HIDE_PIN, 'true');
    setIsPinHidden(true);
  }, []);

  const handleShowPin = useCallback(() => {
    sessionStorage.setItem(SessionKeys.HIDE_PIN, 'false');
    setIsPinHidden(false);
  }, []);

  useEffect(() => {
    Authentication.Instance.registerShowPinCallback(handleShowPin);

    return () => {
      Authentication.Instance.unregisterShowPinCallback();
    };
  }, [handleShowPin]);

  const userLoadedEvent = useCallback(
    (user?: User, isInitial = false) => {
      Authentication.Instance.isAuthenticated()
        .then(async (isAuth) => {
          const loggedIn = !(!user || !user.access_token) && !user.expired;
          if (loggedIn !== isAuth && !isInitial) {
            ai.trackTrace(
              `AuthProvider, User from userloadedevent has a different 'isAuthenticated' value ${loggedIn} - ${isAuth}`,
            );
          }

          if (!isAuth) {
            const isSessionActive = Authentication.Instance.isSessionActive();

            if (isSessionActive) {
              ai.trackTrace('AuthProvider, userLoadedEvent: showing PIN');
              handleShowPin();
            } else {
              ai.trackTrace('AuthProvider: calling login as session is invalid and status not authenticated');
              await Authentication.Instance.logoutKeepingStorageSettings();
              handleHidePin();
              if (isResident) {
                ai.trackTrace('AuthProvider: calling initiateResidentLogin');
                await ResidentAuthentication.Instance.initiateResidentLogin();
              } else if (isNative) {
                const publicKey = await getPublicKey();
                const deviceId = DeviceInfoManager.Instance.getDeviceId();
                const arcValues = [`deviceId:${deviceId}`, `publicKey:${base64url(publicKey)}`].join(' ');
                ai.trackTrace(`AuthProvider mount: calling login(arcValues), deviceId ${deviceId}`);
                await Authentication.Instance.login(arcValues);
              } else {
                ai.trackTrace('AuthProvider mount: calling login()');
                await Authentication.Instance.login();
              }
            }
          } else {
            setIsInitiallyAuthenticated(true);
          }
        })
        .catch((e) => {
          ai.trackException(e, `Unable to succeed login, refreshing page`);
        });
    },
    [isNative, isResident, handleShowPin, handleHidePin],
  );

  useEffect(() => {
    Authentication.Instance.addUserLoaded(userLoadedEvent);

    return () => {
      Authentication.Instance.removeUserLoaded(userLoadedEvent);
    };
  }, [userLoadedEvent]);

  useEffect(() => {
    if (window.location.hash.indexOf('code') !== -1 && window.location.hash.indexOf('/nexuslogin.html') === -1) {
      Authentication.Instance.authenticate().catch((e) => {
        ai.trackException(e, `Authentication failed, forcing logout`);
        Authentication.Instance.logout();
      });
      return;
    }

    userLoadedEvent(undefined, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePinAccepted = useCallback(async (): Promise<void> => {
    ai.startTrackPage(ApplicationInsightTrackKeys.PERFORMANCE_PIN_TO_BOTTOMBAR);
    ai.startTrackPage(ApplicationInsightTrackKeys.PERFORMANCE_PIN_TO_TASKLIST);
    await Authentication.Instance.signinSilent();

    handleHidePin();
    setIsInitiallyAuthenticated(true);
  }, [handleHidePin]);

  if (!isInitiallyAuthenticated && isPinHidden) return <LoadingSpinner />;

  return (
    <AuthContext.Provider value={{ isPinHidden, showPin: handleShowPin, hidePin: handleHidePin }}>
      {!isPinHidden && !isResidentEntrance ? (
        <PinWrapper onPinAccepted={handlePinAccepted} onPinSuccess={handleHidePin} />
      ) : (
        <SilentRefresh onShowPin={handleShowPin}>{children}</SilentRefresh>
      )}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
